import { useEffect } from 'react';
import { Link } from 'react-router';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@monksflow/monks-ui';
import { useQueryClient } from '@tanstack/react-query';
import { ExternalLinkIcon } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useWidgetStore } from '@/app/store/widget';
import { PROJECT_ARTIFACT_QUERY_KEYS } from '@/entities/project-artifact';
import { ROUTES } from '@/shared/config/routes';
import { useArtifactPreviewStore } from '../../store';
import { ArtifactPreviewBody } from './ArtifactPreviewBody';
import type { ArtifactPreviewModalProps } from './ArtifactPreviewModal.types';
import { ArtifactPreviewNavigation } from './ArtifactPreviewNavigation';
import { extractStatus } from './extractStatus';
import { useChatSessionAccess } from './hooks/useChatSessionAccess';
import { useProjectArtifactDetail } from './hooks/useProjectArtifactDetail';
import { isPortaledNonDismissTarget } from './outsideClick.utils';
import { useTranslation } from 'react-i18next';

/**
 * Library artifact preview modal. Hosts the same widget renderers used by the
 * chat side panel inside a Radix Dialog (monks-ui) so the modal mirrors
 * chat behavior with focus trap, ESC-to-close, and focus-restoration provided
 * by the primitive.
 *
 * Header layout mirrors the Library card / Add-existing picker NameCell:
 * - Agent artifacts → primary = chat contextName, secondary = "<widget> · v<n>".
 * - File artifacts (creatorType=user) → primary = filename, secondary = chat contextName.
 * Primary text is wrapped in `<DialogTitle>` for the dialog's accessible name.
 *
 * While the dialog is open the ambient `useWidgetRenderStore` mode is flipped
 * to `'preview'` so reused chat widgets (ImageCard, …) can hide composer-only
 * actions; mode resets to `'chat'` on close.
 */
export function ArtifactPreviewModal(props: ArtifactPreviewModalProps) {
  const { t } = useTranslation('projects');

  const { openArtifactId, openArtifactName } = useArtifactPreviewStore(
    useShallow((s) => ({
      openArtifactId: s.openArtifactId,
      openArtifactName: s.openArtifactName,
    })),
  );

  const queryClient = useQueryClient();
  const detailQuery = useProjectArtifactDetail(props.projectId, openArtifactId);
  const detail = detailQuery.data ?? null;

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) return;
    // If the detail query is sitting in 404 state, the artifact was
    // deleted/detached after the grid rendered. Invalidate the list before
    // closing so the stale card drops on next paint regardless of which
    // dismiss path the user used (ESC, backdrop, X, or the error-state
    // Close button).
    if (openArtifactId !== null) {
      const detailKey = PROJECT_ARTIFACT_QUERY_KEYS.detail(props.projectId, openArtifactId);
      const state = queryClient.getQueryState(detailKey);
      if (state?.error && extractStatus(state.error) === 404) {
        void queryClient.invalidateQueries({
          queryKey: PROJECT_ARTIFACT_QUERY_KEYS.listsForProject(props.projectId),
        });
      }
    }
    useArtifactPreviewStore.getState().close();
  };

  // Gate `open` on both id AND name so the `DialogTitle` never renders empty
  // (WCAG 4.1.2 — accessible name required for the dialog landmark).
  const isOpen = openArtifactId !== null && openArtifactName !== null;

  // Ambient render-mode flip on the shared widget store: while the modal is
  // open, widget renderers (ImageCard, …) read `renderMode === 'preview'` and
  // drop composer-bound actions. Resets on close so the chat side keeps the
  // default `'chat'` affordances.
  useEffect(() => {
    if (!isOpen) return;
    useWidgetStore.getState().setRenderMode('preview');
    return () => useWidgetStore.getState().setRenderMode('chat');
  }, [isOpen]);

  // Mirror ArtifactCard + ArtifactNameCell. Fall back to the store snapshot
  // while the detail query is still loading so the header isn't empty.
  const isFile = detail?.creatorType === 'user';
  const contextName = detail?.originatingContextName ?? null;
  const artifactName = detail?.artifactName ?? openArtifactName ?? '';
  const version = detail?.artifactVersion ?? null;
  const primary = isFile ? artifactName : (contextName ?? artifactName);
  const secondary = isFile ? contextName : contextName != null ? artifactName : null;

  // Probe `GET /contexts/:contextId` to decide whether the originating chat
  // session is reachable for the current user. The api is the source of
  // truth — we never mirror its ownership rules on FE. Loading + every error
  // state collapses to "hide" so an api outage cannot break the preview.
  const chatAccess = useChatSessionAccess(detail?.originatingContextId ?? null);
  const chatHref =
    chatAccess.isSuccess && detail?.originatingContextId != null
      ? ROUTES.CHAT_SESSION(detail.originatingContextId)
      : null;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      {/*
        Frost-glass backdrop via the `overlayClassName` prop on the
        `<DialogContent>` shortcut (monks-ui beta.19). This
        replaced an earlier manual `DialogPortal + DialogOverlay +
        DialogPrimitive.Content` re-composition that existed only because the
        shortcut had no slot to theme the overlay. The built-in close button
        (`showCloseButton` defaults to `true`) and forwarded Radix content
        props (`onPointerDownOutside`) come through the shortcut, so the modal
        keeps byte-identical behavior with far less surface.
      */}
      <DialogContent
        size="lg"
        overlayClassName="bg-black/20 backdrop-blur-md"
        className="flex h-[90vh] w-[min(calc(100vw-2rem),64rem)] flex-col gap-0 overflow-hidden p-0"
        onPointerDownOutside={(event) => {
          if (isPortaledNonDismissTarget(event.target)) {
            event.preventDefault();
          }
        }}
      >
        <DialogHeader className="sticky top-0 flex flex-row items-center gap-3 border-b border-border bg-background p-4 pr-20">
          <ArtifactPreviewNavigation projectId={props.projectId} />
          {/* min-h matches the tallest variant (title + secondary row) so the
              header doesn't shift when navigating between artifacts that have
              and lack the secondary line. */}
          <div className="flex min-h-[56px] min-w-0 flex-1 flex-col items-center justify-center gap-0.5">
            <DialogTitle className="line-clamp-2 text-base font-medium text-foreground">
              {primary}{' '}
              {chatHref !== null && (
                // `m-1` keeps `ring-offset-2` from clipping against the
                // `DialogTitle` edge per `conventions/focus-ring-offset`.
                <Button
                  asChild
                  variant="ghost"
                  size="mini"
                  title={t('projectDetail.artifactPreviewModal.openChatSessionInNew')}
                  className="m-1"
                >
                  <Link to={chatHref} target="_blank" rel="noopener noreferrer">
                    <ExternalLinkIcon className="size-3" />
                  </Link>
                </Button>
              )}
            </DialogTitle>
            {secondary !== null && (
              <div className="flex min-w-0 items-center gap-1.5">
                <span className="line-clamp-1 text-xs text-muted-foreground">{secondary}</span>
                {!isFile && version != null && (
                  <Badge variant="outline" roundness="full" className="shrink-0 py-0">
                    {t('projectDetail.artifactPreviewModal.versionBadge', { version })}
                  </Badge>
                )}
              </div>
            )}
          </div>
        </DialogHeader>
        <div className="flex min-h-0 flex-1 flex-col overflow-auto">
          {openArtifactId !== null && (
            <ArtifactPreviewBody projectId={props.projectId} artifactId={openArtifactId} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
